/**
 * Sign In Form Handler
 * Handles password visibility toggle and input clearing
 */

function initSignInForm() {
  const signInForm = document.querySelector('.sign-in-form');

  if (!signInForm) {
    return;
  }

  // Set forgot password link URL
  const forgotPasswordLink = signInForm.querySelector('.forgot-password-link');
  if (forgotPasswordLink) {
    const adminBaseUrl = typeof ADMIN_BASE_URL !== 'undefined' ? ADMIN_BASE_URL : (window.ADMIN_BASE_URL || 'https://admin-dev.electhq.net');
    forgotPasswordLink.href = `${adminBaseUrl}/en/forgot-password`;
  }

  // Helper function to show field error
  function showFieldError(input, message) {
    const fieldName = input.name;
    const errorElement = document.getElementById(`${fieldName}_error`);
    const inputWrapper = input.closest('.input-wrapper');
    
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.classList.add('show');
      errorElement.style.color = '#DC2626'; // Ensure red color
    }
    
    if (inputWrapper) {
      input.classList.add('input-error');
    }
  }

  // Helper function to clear field error
  function clearFieldError(input) {
    const fieldName = input.name;
    const errorElement = document.getElementById(`${fieldName}_error`);
    
    if (errorElement) {
      errorElement.textContent = '';
      errorElement.classList.remove('show');
    }
    
    input.classList.remove('input-error');
  }

  // Password visibility toggle
  const passwordToggle = signInForm.querySelector('.password-toggle');
  const passwordInput = signInForm.querySelector('#password');
  const eyeIcon = signInForm.querySelector('.eye-icon');
  const eyeOffIcon = signInForm.querySelector('.eye-off-icon');

  if (passwordToggle && passwordInput) {
    passwordToggle.addEventListener('click', function () {
      const isPassword = passwordInput.type === 'password';
      passwordInput.type = isPassword ? 'text' : 'password';

      if (eyeIcon && eyeOffIcon) {
        if (isPassword) {
          eyeIcon.style.display = 'none';
          eyeOffIcon.style.display = 'block';
        } else {
          eyeIcon.style.display = 'block';
          eyeOffIcon.style.display = 'none';
        }
      }
    });
  }

  // Clear input buttons
  const clearButtons = signInForm.querySelectorAll('.input-clear');
  clearButtons.forEach((button) => {
    button.addEventListener('click', function () {
      const inputWrapper = button.closest('.input-wrapper');
      const input = inputWrapper.querySelector('input');
      if (input) {
        input.value = '';
        input.focus();
        // Clear error when input is cleared
        clearFieldError(input);
      }
    });
  });

  // Show/hide clear buttons and asterisks based on input value
  const inputs = signInForm.querySelectorAll('.input-wrapper input');
  inputs.forEach((input) => {
    const clearButton = input.closest('.input-wrapper').querySelector('.input-clear');
    const asterisk = input.closest('.input-wrapper').querySelector('.input-asterisk');
    
    // Position asterisk after placeholder text
    function positionAsterisk() {
      if (asterisk && input.placeholder) {
        // Create a temporary span to measure placeholder text width
        const tempSpan = document.createElement('span');
        tempSpan.style.visibility = 'hidden';
        tempSpan.style.position = 'absolute';
        tempSpan.style.fontSize = window.getComputedStyle(input).fontSize;
        tempSpan.style.fontFamily = window.getComputedStyle(input).fontFamily;
        tempSpan.style.fontWeight = window.getComputedStyle(input).fontWeight;
        tempSpan.textContent = input.placeholder;
        document.body.appendChild(tempSpan);
        
        const textWidth = tempSpan.offsetWidth;
        document.body.removeChild(tempSpan);
        
        // Position asterisk: icon width (20px) + icon left padding (16px) + text width + small gap
        const iconLeft = 16;
        const iconWidth = 20;
        const gap = 4;
        asterisk.style.left = `${iconLeft + iconWidth + textWidth + gap + 15}px`;
      }
    }
    
    function toggleClearButton() {
      if (clearButton) {
        clearButton.style.display = input.value ? 'flex' : 'none';
      }
    }

    function toggleAsterisk() {
      if (asterisk) {
        // Show asterisk only when input is empty (placeholder is visible)
        asterisk.style.opacity = input.value ? '0' : '1';
      }
    }

    // Position asterisk on load and when window resizes
    positionAsterisk();
    window.addEventListener('resize', positionAsterisk);

    input.addEventListener('input', function() {
      toggleClearButton();
      toggleAsterisk();
    });
    input.addEventListener('focus', function() {
      toggleClearButton();
      toggleAsterisk();
    });
    input.addEventListener('blur', toggleAsterisk);
    
    toggleClearButton(); // Initial check
    toggleAsterisk(); // Initial check
  });

  // Clear errors when user starts typing
  inputs.forEach((input) => {
    input.addEventListener('input', function() {
      clearFieldError(input);
    });
  });

  // Form submission handler
  signInForm.addEventListener('submit', async function (e) {
    e.preventDefault();

    const emailInput = signInForm.querySelector('#email');
    const passwordInput = signInForm.querySelector('#password');
    const submitButton = signInForm.querySelector('.btn-sign-in');

    const email = emailInput.value.trim();
    const password = passwordInput.value;

    // Clear previous errors
    clearFieldError(emailInput);
    clearFieldError(passwordInput);

    // Validate email
    if (!email) {
      showFieldError(emailInput, 'Please input your email');
    }

    // Validate password
    if (!password) {
      showFieldError(passwordInput, 'Please input your password');
    }

    // If validation fails, stop submission
    if (!email || !password) {
      // Scroll to first error field
      const firstErrorField = signInForm.querySelector('.input-error');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstErrorField.focus();
      }
      return;
    }

    // Disable submit button and show loading state
    const originalButtonText = submitButton.textContent;
    submitButton.disabled = true;
    submitButton.textContent = 'Signing in...';

    try {
      // Get API base URL from global scope (defined in main.js)
      const apiBaseUrl = typeof API_BASE_URL !== 'undefined' ? API_BASE_URL : (window.API_BASE_URL || 'https://api.electhq.net');
      
      const response = await fetch(`${apiBaseUrl}/auth/login-no-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password,
        }),
      });

      let data = {};
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      }

      if (response.ok) {
        // Success: Login successful, extract token from response
        const token = data.token || data.accessToken || data.access_token || data.data?.token;
        
        if (token) {
          // Redirect to admin panel with token
          const adminBaseUrl = typeof ADMIN_BASE_URL !== 'undefined' ? ADMIN_BASE_URL : (window.ADMIN_BASE_URL || 'https://admin-dev.electhq.net');
          window.location.href = `${adminBaseUrl}?token=${encodeURIComponent(token)}`;
        } else {
          // Token not found in response
          console.error('Token not found in response:', data);
          alert('Login successful but token not received. Please contact support.');
        }
      } else if (response.status === 401) {
        // Invalid credentials
        alert('Invalid credentials. Please check your email and password and try again.');
        passwordInput.value = '';
        passwordInput.focus();
      } else if (response.status === 500) {
        // Server error
        alert('Server error. Please try again later.');
      } else {
        // Other errors
        const errorMessage = data.message || 'An error occurred. Please try again.';
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Sign in error:', error);
      
      // Get API base URL for debug info
      const apiBaseUrl = typeof API_BASE_URL !== 'undefined' ? API_BASE_URL : (window.API_BASE_URL || 'https://api.electhq.net');
      
      // Use shared error modal utility
      if (typeof window.showErrorModal === 'function') {
        await window.showErrorModal(error, `${apiBaseUrl}/auth/login-no-otp`, {
          title: 'Sign In Error',
          message: 'Network error. Please check your connection and try again.',
          additionalInfo: {
            formType: 'sign-in',
            email: email,
          },
        });
      } else {
        // Fallback if utility not loaded
        alert('Network error. Please check your connection and try again.');
      }
    } finally {
      // Re-enable submit button
      submitButton.disabled = false;
      submitButton.textContent = originalButtonText;
    }
  });
}

// Export to window for global access
window.initSignInForm = initSignInForm;

